import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { OwnerBadge, OwnerFilter, HiddenMonitorsModal, fetchHidden } from './MonitorOwnership';

const IconPlus = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
       strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
    <line x1="12" y1="5" x2="12" y2="19"/>
    <line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);

const IconTrash = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6l-1 14H6L5 6"/>
    <path d="M10 11v6"/>
    <path d="M14 11v6"/>
    <path d="M9 6V4h6v2"/>
  </svg>
);

const IconEdit = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

const IconSearch = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/>
    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);

const IconActivity = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
  </svg>
);

const IconKey = ({ size = 13 }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round" width={size} height={size}>
    <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>
  </svg>
);

function getMonitorStatus(monitor) {
  if (monitor.status === 'up') return true;
  if (monitor.status === 'down') return false;
  return false;
}

const EMPTY_FORM = { name: '', hostname: '', ip: '', port: '', keyword: '', keyword_mode: 'not_exists', keyword_case_sensitive: false };

function UptimeMonitor({ token, user }) {
  const [monitors, setMonitors]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [showModal, setShowModal]       = useState(false);
  const [formData, setFormData]         = useState(EMPTY_FORM);
  const [editMonitor, setEditMonitor]   = useState(null);
  const [error, setError]               = useState('');
  const [searchTerm, setSearchTerm]     = useState('');
  const [statusFilter, setStatusFilter] = useState(''); // '' | 'online' | 'offline'
  const [ownerFilter, setOwnerFilter]   = useState(''); // admin only: '' | 'default' | '<userId>'
  const [hidden, setHidden]             = useState([]);
  const [showHidden, setShowHidden]     = useState(false);

  const canEdit = user?.role === 'admin' || user?.role === 'editor';
  // Admin looking at another user's private monitors: view only.
  const viewingOtherUser = ownerFilter !== '' && ownerFilter !== 'default';

  const fetchMonitors = useCallback(async () => {
    try {
      const res = await api.get('/uptime', { params: ownerFilter ? { owner: ownerFilter } : {} });
      setMonitors(res.data);
      if (canEdit) setHidden(await fetchHidden('uptime'));
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch monitors:', err);
      setLoading(false);
    }
  }, [ownerFilter, canEdit]);

  useEffect(() => {
    fetchMonitors();
    const interval = setInterval(fetchMonitors, 60000);
    return () => clearInterval(interval);
  }, [fetchMonitors]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (editMonitor) {
        await api.put('/uptime/' + editMonitor.id, formData);
      } else {
        await api.post('/uptime', formData);
      }
      setShowModal(false);
      setEditMonitor(null);
      setFormData(EMPTY_FORM);
      fetchMonitors();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save monitor');
    }
  };

  // Deletes a monitor, or for an editor on a default, removes it from their view.
  const handleDelete = async (monitor) => {
    const hide = monitor.delete_action === 'hide';
    const question = hide
      ? 'Delete "' + monitor.name + '"? It will be removed from your account only.'
      : monitor.is_default
        ? 'Delete default monitor "' + monitor.name + '"? It will be removed for everyone.'
        : 'Delete monitor "' + monitor.name + '"?';
    if (!window.confirm(question)) return;
    try {
      await api.delete('/uptime/' + monitor.id);
      fetchMonitors();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete monitor');
    }
  };

  const openAddModal = () => {
    setFormData(EMPTY_FORM);
    setEditMonitor(null);
    setError('');
    setShowModal(true);
  };

  const openEditModal = (monitor) => {
    setFormData({
      name:                   monitor.name,
      hostname:               monitor.hostname || '',
      ip:                     monitor.ip || '',
      port:                   monitor.port,
      keyword:                monitor.keyword || '',
      keyword_mode:           monitor.keyword_mode || 'not_exists',
      keyword_case_sensitive: monitor.keyword_case_sensitive || false,
    });
    setEditMonitor(monitor);
    setError('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditMonitor(null);
    setError('');
  };

  const filteredMonitors = monitors.filter(m => {
    const matchSearch =
      m.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.hostname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.ip?.toLowerCase().includes(searchTerm.toLowerCase());
    let matchStatus = true;
    if (statusFilter) {
      const isUp = getMonitorStatus(m);
      matchStatus = statusFilter === 'online' ? isUp : !isUp;
    }
    return matchSearch && matchStatus;
  });

  const upCount   = monitors.filter(m => getMonitorStatus(m)).length;
  const downCount = monitors.filter(m => !getMonitorStatus(m)).length;

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner" />
        <span>Loading monitors…</span>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div className="page-header-top">
          <div>
            <h1>Uptime Monitor</h1>
            <p>Track server and service availability in real time</p>
          </div>
          {canEdit && (
            <div className="page-actions">
              <button className="btn btn-secondary" onClick={() => setShowHidden(true)}>
                Deleted ({hidden.length})
              </button>
              {!viewingOtherUser && (
                <button className="btn btn-primary" onClick={openAddModal}>
                  <IconPlus /> Add Monitor
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {monitors.length > 0 && (
        <div className="cards-grid" style={{ marginBottom: '20px' }}>
          <div className="card" style={{ cursor: 'pointer' }}
               onClick={() => setStatusFilter('')}>
            <div className="card-header">
              <span className="card-title">Total Monitors</span>
              <div className="card-icon card-icon-primary"><IconActivity /></div>
            </div>
            <div className="card-value">{monitors.length}</div>
            <div className="card-change">Configured endpoints</div>
          </div>
          <div className="card" style={{ cursor: 'pointer', ...(statusFilter === 'online' ? { borderColor: 'var(--primary)' } : {}) }}
               onClick={() => setStatusFilter(prev => prev === 'online' ? '' : 'online')}>
            <div className="card-header">
              <span className="card-title">Online</span>
            </div>
            <div className="card-value" style={{ color: 'var(--success)' }}>{upCount}</div>
            <div className="card-change positive">Responding normally</div>
            {statusFilter === 'online' && (
              <div style={{ fontSize: '10px', color: 'var(--primary)', fontWeight: 600, marginTop: 4 }}>Filtering ×</div>
            )}
          </div>
          <div className="card" style={{ cursor: 'pointer', ...(statusFilter === 'offline' ? { borderColor: 'var(--primary)' } : {}) }}
               onClick={() => setStatusFilter(prev => prev === 'offline' ? '' : 'offline')}>
            <div className="card-header">
              <span className="card-title">Offline</span>
            </div>
            <div className="card-value" style={{ color: downCount > 0 ? 'var(--danger)' : 'var(--text-muted)' }}>
              {downCount}
            </div>
            <div className={'card-change ' + (downCount > 0 ? 'negative' : '')}>
              {downCount > 0 ? 'Needs attention' : 'All clear'}
            </div>
            {statusFilter === 'offline' && (
              <div style={{ fontSize: '10px', color: 'var(--primary)', fontWeight: 600, marginTop: 4 }}>Filtering ×</div>
            )}
          </div>
          <div className="card">
            <div className="card-header">
              <span className="card-title">Availability</span>
            </div>
            <div className="card-value" style={{ fontSize: '26px' }}>
              {monitors.length > 0 ? Math.round((upCount / monitors.length) * 100) : 100}%
            </div>
            <div className="progress-bar" style={{ marginTop: '12px' }}>
              <div
                className={'progress-fill ' + (
                  upCount / monitors.length >= 0.9 ? 'progress-success' :
                  upCount / monitors.length >= 0.7 ? 'progress-warning' : 'progress-danger'
                )}
                style={{ width: (monitors.length > 0 ? (upCount / monitors.length) * 100 : 100) + '%' }}
              />
            </div>
          </div>
        </div>
      )}

      <div className="table-container">
        <div className="table-header">
          <h3>Monitors</h3>
          <div className="table-toolbar">
            <OwnerFilter user={user} value={ownerFilter} onChange={setOwnerFilter} />
            <div className="search-input-wrap">
              <IconSearch />
              <input
                type="text"
                placeholder="Search monitors…"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {filteredMonitors.length === 0 ? (
          <div className="table-empty">
            <div className="table-empty-icon"><IconActivity /></div>
            <div className="table-empty-title">
              {searchTerm || statusFilter ? 'No monitors found' : 'No monitors yet'}
            </div>
            <div className="table-empty-desc">
              {searchTerm || statusFilter ? 'Try a different search or clear the filter' : 'Add a monitor to start tracking uptime'}
            </div>
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Hostname</th>
                <th>IP Address</th>
                <th>Port</th>
                <th>Status</th>
                <th>Response</th>
                <th>Interval</th>
                {canEdit && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {filteredMonitors.map(monitor => {
                var isUp = getMonitorStatus(monitor);
                return (
                <tr key={monitor.id}>
                  <td style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
                    {monitor.name}
                    <span style={{ marginLeft: 8, verticalAlign: 'middle' }}><OwnerBadge monitor={monitor} /></span>
                    {monitor.keyword && (
                      <span
                        title={'Keyword: "' + monitor.keyword + '" (' + (monitor.keyword_mode === 'exists' ? 'alert when exists' : 'alert when not exists') + ')'}
                        style={{ marginLeft: 6, opacity: 0.55, cursor: 'help',
                                 display: 'inline-flex', verticalAlign: 'middle',
                                 color: 'var(--primary)' }}
                      >
                        <IconKey size={12} />
                      </span>
                    )}
                  </td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: '12px' }}>
                    {monitor.hostname || <span style={{ color: 'var(--text-muted)' }}>-</span>}
                  </td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: '12px' }}>
                    {monitor.ip || <span style={{ color: 'var(--text-muted)' }}>-</span>}
                  </td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: '12px', fontWeight: '500' }}>
                    {monitor.port}
                  </td>
                  <td>
                    <div className="status-with-dot">
                      <span className={'status-dot ' + (isUp ? 'status-dot-up' : 'status-dot-down')} />
                      <span className={'status-badge ' + (isUp ? 'status-up' : 'status-down')}>
                        {isUp ? 'Online' : 'Offline'}
                      </span>
                    </div>
                  </td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: '12px', color: 'var(--text-muted)' }}>
                    {monitor.last_response_time ? monitor.last_response_time + 'ms' : '-'}
                  </td>
                  <td style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
                    {monitor.check_interval}s
                  </td>
                  {canEdit && (
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {monitor.can_edit && (
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => openEditModal(monitor)}
                          >
                            <IconEdit /> Edit
                          </button>
                        )}
                        {monitor.delete_action === 'delete' && (
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => handleDelete(monitor)}
                          >
                            <IconTrash /> Delete
                          </button>
                        )}
                        {monitor.delete_action === 'hide' && (
                          <button
                            className="btn btn-danger btn-sm"
                            title="Delete from your account only"
                            onClick={() => handleDelete(monitor)}
                          >
                            <IconTrash /> Delete
                          </button>
                        )}
                        {!monitor.can_edit && !monitor.delete_action && (
                          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Read-only</span>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {filteredMonitors.length > 0 && (
          <div className="table-footer">
            Showing {filteredMonitors.length} of {monitors.length} monitors
            {statusFilter && (
              <button onClick={() => setStatusFilter('')}
                style={{ marginLeft: 10, color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                Clear filter
              </button>
            )}
          </div>
        )}
      </div>

      {showHidden && (
        <HiddenMonitorsModal
          type="uptime"
          items={hidden}
          describe={m => m.name + ' — ' + (m.hostname || m.ip) + ':' + m.port}
          onClose={() => setShowHidden(false)}
          onRestored={fetchMonitors}
        />
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editMonitor ? 'Edit Uptime Monitor' : 'Add Uptime Monitor'}</h3>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>

            <div className="modal-body">
              {error && <div className="alert alert-error">{error}</div>}
              {!editMonitor && (
                <div className="form-hint" style={{ marginBottom: 12 }}>
                  {user?.role === 'admin'
                    ? 'Monitors you add are defaults, visible to every user.'
                    : 'Monitors you add are private: only you see them and get their alerts.'}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>
                    Monitor Name
                    <span className="label-hint">*required</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="e.g. Production Web Server"
                    autoFocus
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Hostname</label>
                    <input
                      type="text"
                      value={formData.hostname}
                      onChange={e => setFormData({ ...formData, hostname: e.target.value })}
                      placeholder="example.com"
                    />
                  </div>
                  <div className="form-group">
                    <label>IP Address</label>
                    <input
                      type="text"
                      value={formData.ip}
                      onChange={e => setFormData({ ...formData, ip: e.target.value })}
                      placeholder="192.168.1.1"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>
                    Port
                    <span className="label-hint">*required</span>
                  </label>
                  <input
                    type="number"
                    value={formData.port}
                    onChange={e => setFormData({ ...formData, port: e.target.value })}
                    required
                    placeholder="80"
                    min="1"
                    max="65535"
                  />
                  <div className="form-hint">Common: 80 (HTTP), 443 (HTTPS), 22 (SSH)</div>
                </div>

                <div style={{ borderTop: '1px solid var(--border)', marginTop: 16, paddingTop: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ display: 'inline-flex', color: 'var(--primary)' }}><IconKey size={15} /></span>
                    Keyword Monitoring
                    <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-muted)' }}>optional</span>
                  </div>

                  <div className="form-group">
                    <label>Keyword to look for</label>
                    <input
                      type="text"
                      value={formData.keyword || ''}
                      onChange={e => setFormData({ ...formData, keyword: e.target.value })}
                      placeholder="e.g. 502, Welcome, Login"
                    />
                    <div className="form-hint">The keyword must be present in the response HTML source. HTTP/HTTPS only.</div>
                  </div>

                  {formData.keyword && formData.keyword.trim() !== '' && (
                    <>
                      <div className="form-group">
                        <label>Alert condition</label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          <label style={{
                            display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                            border: '1px solid ' + (formData.keyword_mode === 'exists' ? 'var(--danger)' : 'var(--border)'),
                            borderRadius: 'var(--radius-md, 8px)', cursor: 'pointer',
                            background: formData.keyword_mode === 'exists' ? 'rgba(239,68,68,0.05)' : 'transparent',
                            transition: 'all 0.2s ease',
                          }}>
                            <input
                              type="radio"
                              name="keyword_mode"
                              checked={formData.keyword_mode === 'exists'}
                              onChange={() => setFormData({ ...formData, keyword_mode: 'exists' })}
                              style={{ accentColor: 'var(--danger)' }}
                            />
                            <div>
                              <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>Alert when keyword EXISTS</div>
                              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                                Incident starts when "{formData.keyword}" is found (e.g., error pages, 502)
                              </div>
                            </div>
                          </label>

                          <label style={{
                            display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                            border: '1px solid ' + (formData.keyword_mode === 'not_exists' ? 'var(--primary)' : 'var(--border)'),
                            borderRadius: 'var(--radius-md, 8px)', cursor: 'pointer',
                            background: formData.keyword_mode === 'not_exists' ? 'var(--primary-subtle)' : 'transparent',
                            transition: 'all 0.2s ease',
                          }}>
                            <input
                              type="radio"
                              name="keyword_mode"
                              checked={formData.keyword_mode === 'not_exists'}
                              onChange={() => setFormData({ ...formData, keyword_mode: 'not_exists' })}
                              style={{ accentColor: 'var(--primary)' }}
                            />
                            <div>
                              <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>Alert when keyword does NOT exist</div>
                              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                                Incident starts when "{formData.keyword}" is missing (e.g., expected content gone)
                              </div>
                            </div>
                          </label>
                        </div>
                      </div>

                      <div className="form-group">
                        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none' }}>
                          <div
                            style={{
                              width: 40, height: 22, borderRadius: 11, position: 'relative', flexShrink: 0,
                              background: formData.keyword_case_sensitive ? 'var(--primary)' : 'var(--border)',
                              transition: 'background 0.2s ease', cursor: 'pointer',
                            }}
                            onClick={() => setFormData({ ...formData, keyword_case_sensitive: !formData.keyword_case_sensitive })}
                          >
                            <div style={{
                              width: 18, height: 18, borderRadius: '50%', background: 'white',
                              position: 'absolute', top: 2,
                              left: formData.keyword_case_sensitive ? 20 : 2,
                              transition: 'left 0.2s ease', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                            }} />
                          </div>
                          <span style={{ fontSize: 13, color: 'var(--text-primary)' }}>Case-sensitive check</span>
                        </label>
                      </div>
                    </>
                  )}
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeModal}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editMonitor ? 'Save Changes' : 'Add Monitor'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UptimeMonitor;
